import { useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import type { OnboardingDraft } from './types';

export type BoostModalKind = 'certifications' | 'accomplishments' | 'portfolio';

type OnboardingBoostModalProps = {
  kind: BoostModalKind | null;
  draft: OnboardingDraft;
  saving?: boolean;
  onHide: () => void;
  onSave: (patch: Partial<OnboardingDraft>, successMessage: string) => Promise<boolean>;
};

const TITLES: Record<BoostModalKind, string> = {
  certifications: 'Add certification',
  accomplishments: 'Add accomplishment',
  portfolio: 'Add portfolio link',
};

const SUCCESS_COPY: Record<BoostModalKind, string> = {
  certifications: 'Certification added to your profile.',
  accomplishments: 'Accomplishment added to your profile.',
  portfolio: 'Portfolio link added to your profile.',
};

export default function OnboardingBoostModal({
  kind,
  draft,
  saving = false,
  onHide,
  onSave,
}: OnboardingBoostModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [url, setUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setName('');
    setDescription('');
    setDate('');
    setUrl('');
  };

  const handleClose = () => {
    reset();
    onHide();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!kind) return;

    let patch: Partial<OnboardingDraft> | null = null;

    if (kind === 'certifications') {
      if (!name.trim()) {
        toast.error('Enter a certification name.');
        return;
      }
      patch = {
        certifications: [...(draft.certifications || []), { name: name.trim(), date: date.trim() }],
      };
    } else if (kind === 'accomplishments') {
      if (!name.trim()) {
        toast.error('Enter an accomplishment title.');
        return;
      }
      patch = {
        accomplishments: [
          ...(draft.accomplishments || []),
          { name: name.trim(), description: description.trim(), date: date.trim() },
        ],
      };
    } else if (kind === 'portfolio') {
      if (!url.trim()) {
        toast.error('Enter a portfolio URL.');
        return;
      }
      const label = name.trim() || 'Portfolio link';
      patch = {
        projects: [
          ...(draft.projects || []),
          { name: label, description: description.trim(), link: url.trim() },
        ],
      };
    }

    if (!patch) return;

    setSubmitting(true);
    try {
      const saved = await onSave(patch, SUCCESS_COPY[kind]);
      if (saved) {
        handleClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const busy = saving || submitting;

  return (
    <Modal show={Boolean(kind)} onHide={handleClose} centered className="ss-app-modal">
      <Modal.Header closeButton>
        <Modal.Title>{kind ? TITLES[kind] : ''}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={(event) => void handleSubmit(event)}>
        <Modal.Body className="ssr-modal-body">
          {kind === 'certifications' ? (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Certification name</Form.Label>
                <Form.Control
                  className="ssr-field-input"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="e.g. AWS Solutions Architect"
                  required
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Date (optional)</Form.Label>
                <Form.Control
                  className="ssr-field-input"
                  value={date}
                  onChange={(event) => setDate(event.target.value)}
                  placeholder="e.g. 2024"
                />
              </Form.Group>
            </>
          ) : null}

          {kind === 'accomplishments' ? (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  className="ssr-field-input"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="e.g. Led migration to microservices"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Description (optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  className="ssr-field-input ssr-field-textarea"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Date (optional)</Form.Label>
                <Form.Control
                  className="ssr-field-input"
                  value={date}
                  onChange={(event) => setDate(event.target.value)}
                />
              </Form.Group>
            </>
          ) : null}

          {kind === 'portfolio' ? (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Link URL</Form.Label>
                <Form.Control
                  type="url"
                  className="ssr-field-input"
                  value={url}
                  onChange={(event) => setUrl(event.target.value)}
                  placeholder="https://github.com/you"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Label (optional)</Form.Label>
                <Form.Control
                  className="ssr-field-input"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="GitHub, personal site, etc."
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Description (optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  className="ssr-field-input ssr-field-textarea"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </Form.Group>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="success" type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
